<template>
  <div class="fixed top-20 right-5 z-[9999] flex flex-col items-end gap-3 max-w-sm w-full pointer-events-none">
    <TransitionGroup name="notification">
      <div
        v-for="notification in notifications"
        :key="notification.id"
        :class="[
          'pointer-events-auto w-full rounded-2xl shadow-2xl backdrop-blur-md',
          'border overflow-hidden transition-all duration-300',
          'hover:shadow-3xl hover:-translate-y-0.5 cursor-pointer',
          containerStyles(notification.type),
        ]"
        role="alert"
        @click="notification.onClick ? (notification.onClick(), dismissNotification(notification.id)) : dismissNotification(notification.id)"
      >
        <!-- Top accent bar -->
        <div :class="['h-1 w-full', accentBarColor(notification.type)]"></div>

        <div class="flex items-start gap-3 px-4 pt-3 pb-2">
          <!-- Icon with colored circle background -->
          <div :class="['flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center', iconBgClass(notification.type)]">
            <i :class="['pi text-base', iconClass(notification.type)]"></i>
          </div>

          <!-- Content -->
          <div class="flex-1 min-w-0">
            <p :class="['font-semibold text-sm leading-tight', titleColor(notification.type)]">
              {{ notification.message }}
            </p>
            <p v-if="notification.description" class="text-xs mt-0.5 text-theme-text2 leading-snug whitespace-pre-line">
              {{ notification.description }}
            </p>
          </div>

          <!-- Close button -->
          <button
            @click.stop="dismissNotification(notification.id)"
            class="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-theme-muted hover:text-theme-text hover:bg-theme-bg2 transition-colors"
            aria-label="Close notification"
          >
            <i class="pi pi-times text-xs"></i>
          </button>
        </div>

        <!-- Progress bar -->
        <div v-if="notification.duration > 0" class="px-4 pb-3 pt-1">
          <div class="h-1 rounded-full overflow-hidden bg-theme-bg2">
            <div
              class="h-full rounded-full transition-all duration-100 ease-linear"
              :style="{ width: `${notification.progress}%` }"
              :class="accentBarColor(notification.type)"
            ></div>
          </div>
        </div>
        <div v-else class="pb-3"></div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import useNotifications from '@/store/notification.js';

export default {
  setup() {
    const { notifications, dismissNotification } = useNotifications();
    const intervals = ref({});

    const startProgressTracking = (notification) => {
      if (notification.duration <= 0) return;

      const startTime = Date.now();
      notification.progress = 100;

      intervals.value[notification.id] = setInterval(() => {
        const elapsed = Date.now() - startTime;
        notification.progress = Math.max(0, 100 - (elapsed / notification.duration) * 100);

        if (elapsed >= notification.duration) {
          clearInterval(intervals.value[notification.id]);
        }
      }, 50);
    };

    onMounted(() => {
      notifications.value.forEach(notification => {
        if (notification.duration > 0 && !intervals.value[notification.id]) {
          startProgressTracking(notification);
        }
      });
    });

    onUnmounted(() => {
      Object.values(intervals.value).forEach(interval => clearInterval(interval));
    });

    watch(notifications, (newVal) => {
      const lastNotification = newVal[newVal.length - 1];
      if (lastNotification && lastNotification.duration > 0 && !intervals.value[lastNotification.id]) {
        startProgressTracking(lastNotification);
      }
    }, { deep: true });

    const containerStyles = (type) => {
      const styles = {
        success: 'bg-theme-card/95 border-green-200 dark:border-green-800/40',
        error: 'bg-theme-card/95 border-red-200 dark:border-red-800/40',
        warning: 'bg-theme-card/95 border-primary-200 dark:border-primary-800/40',
        info: 'bg-theme-card/95 border-blue-200 dark:border-blue-800/40',
      };
      return styles[type] || styles.info;
    };

    const accentBarColor = (type) => {
      const colors = {
        success: 'bg-green-500',
        error: 'bg-red-500',
        warning: 'bg-primary-500',
        info: 'bg-blue-500',
      };
      return colors[type] || colors.info;
    };

    const iconBgClass = (type) => {
      const classes = {
        success: 'bg-green-100 dark:bg-green-900/30',
        error: 'bg-red-100 dark:bg-red-900/30',
        warning: 'bg-primary-100 dark:bg-primary-900/30',
        info: 'bg-blue-100 dark:bg-blue-900/30',
      };
      return classes[type] || classes.info;
    };

    const iconClass = (type) => {
      const icons = {
        success: 'pi-check text-green-600 dark:text-green-400',
        error: 'pi-times text-red-600 dark:text-red-400',
        warning: 'pi-exclamation-triangle text-primary-600 dark:text-primary-400',
        info: 'pi-info-circle text-blue-600 dark:text-blue-400',
      };
      return icons[type] || icons.info;
    };

    const titleColor = (type) => {
      const colors = {
        success: 'text-green-800 dark:text-green-200',
        error: 'text-red-800 dark:text-red-200',
        warning: 'text-primary-800 dark:text-primary-200',
        info: 'text-blue-800 dark:text-blue-200',
      };
      return colors[type] || colors.info;
    };

    return {
      notifications,
      dismissNotification,
      containerStyles,
      accentBarColor,
      iconBgClass,
      iconClass,
      titleColor,
    };
  },
};
</script>

<style scoped>
.notification-move,
.notification-enter-active,
.notification-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(60%) scale(0.9);
}

.notification-leave-active {
  position: absolute;
  width: 100%;
}

.shadow-3xl {
  box-shadow: 0 25px 60px -12px rgb(0 0 0 / 0.25);
}
</style>